/**
 * 统一的 onChange 类型，用于适配两类常见输入组件：
 * 1) antd Input：onChange(event) 形式
 * 2) 一些自定义组件：onChange(value) 形式
 */
export type NoSpaceOnChange = (arg1: any, ...rest: any[]) => void;

/**
 * 去除字符串中的所有空白字符
 * - 包含：空格、Tab、换行等（正则 \\s 会匹配所有空白）
 * - 非字符串则原样返回（保证对数字、对象、undefined 等安全）
 */
export const stripAllSpaces = (value: unknown) => {
  if (typeof value !== 'string') return value;
  return value.replace(/\s+/g, '');
};

/**
 * 生成一个“禁空格”的 onChange 包装器
 *
 * 设计目的：
 * - 在“输入过程中”就把空白字符剔除，避免后续各种对比/查询/提交出现隐性 bug
 * - 同时尽量保持组件原有的 onChange 行为不变（只修正 value）
 *
 * 注意：
 * - 该方法适合在单个输入组件上精细控制（例如少数输入框允许空格，其他不允许）
 * - 本次项目已采用更强的全局方案（见 global.tsx），这里作为可复用工具保留
 */
export const createNoSpaceOnChange = (
  onChange?: (...args: any[]) => void,
): NoSpaceOnChange => {
  return (arg1: any, ...rest: any[]) => {
    // 情况 1：标准 DOM 事件（例如 antd Input）
    if (arg1 && typeof arg1 === 'object' && 'target' in arg1) {
      const target = (arg1 as any).target;
      if (target && typeof target.value === 'string') {
        const nextValue = target.value.replace(/\s+/g, '');
        if (nextValue !== target.value) {
          target.value = nextValue;
        }
      }
      onChange?.(arg1, ...rest);
      return;
    }

    // 情况 2：直接回传 value（例如某些自定义组件）
    if (typeof arg1 === 'string') {
      const nextValue = arg1.replace(/\s+/g, '');
      onChange?.(nextValue, ...rest);
      return;
    }

    // 其他情况：原样透传
    onChange?.(arg1, ...rest);
  };
};
